/**
 * I-5F-3 — Two-Factor setup wizard.
 *
 * Three steps:
 *   1. start  — explain + Continue
 *   2. scan   — QR + secret + 6-digit-input + Verify
 *   3. codes  — 10 recovery codes + Download + Copy + Continue
 *
 * Driven by either:
 *  - a `setup_token` from a `setup_required` login response
 *    (force-enrollment from the Login page)
 *  - or a logged-in admin from Settings → Security
 *
 * When confirmation succeeds, `onDone` fires. The parent decides
 * what happens next (Login navigates to `/`, Settings closes the
 * modal + invalidates the device-status query).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth, type ConfirmResponse, type SetupResponse } from '../auth/AuthProvider'
import { OtpInput } from './OtpInput'

interface Props {
  setupToken: string | null   // null → uses session auth (Settings path)
  onDone: () => void
  onCancel?: () => void
}

type Stage = 'start' | 'scan' | 'codes'

export function TwoFactorSetup({ setupToken, onDone, onCancel }: Readonly<Props>) {
  const { t } = useTranslation()
  const auth = useAuth()
  const [stage, setStage] = useState<Stage>('start')
  const [setup, setSetup] = useState<SetupResponse | null>(null)
  const [otp, setOtp] = useState('')
  const [codes, setCodes] = useState<ConfirmResponse | null>(null)
  const [savedAck, setSavedAck] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const startSetup = async () => {
    setBusy(true); setErr(null)
    try {
      const data = await auth.setup2FA(setupToken)
      setSetup(data)
      setStage('scan')
    } catch {
      setErr(t('auth.2fa.setupFailed', 'Konnte 2FA-Setup nicht starten.'))
    } finally { setBusy(false) }
  }

  const submitOtp = async (code: string) => {
    if (!setup) return
    setBusy(true); setErr(null)
    try {
      const data = await auth.confirm2FA(setup.confirm_token, code)
      setCodes(data)
      setStage('codes')
    } catch {
      setErr(t('auth.2fa.invalid', 'Falscher Code — versuche es nochmal.'))
      setOtp('')
    } finally { setBusy(false) }
  }

  const copyCodes = () => {
    if (!codes) return
    navigator.clipboard?.writeText(codes.recovery_codes.join('\n')).catch(() => {})
    setSavedAck(true)
  }

  const downloadCodes = () => {
    if (!codes) return
    const text = [
      'HeilPort — recovery codes',
      `Generated ${new Date().toISOString()}`,
      '',
      'Each code works ONCE. Store this file somewhere safe.',
      '',
      ...codes.recovery_codes,
    ].join('\n')
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'mashfa-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
    setSavedAck(true)
  }

  if (stage === 'start') {
    return (
      <div style={{ padding: 24, maxWidth: 480 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600 }}>
          {t('auth.setup.title', 'Zwei-Faktor-Authentifizierung einrichten')}
        </h2>
        <p style={{ margin: '0 0 16px', color: '#525252', fontSize: 14 }}>
          {t('auth.setup.introduction',
             'Du brauchst eine Authenticator-App (Google Authenticator, Authy, 1Password) auf deinem Telefon.')}
        </p>
        {err && <ErrorBanner msg={err} />}
        <FooterButtons
          onCancel={onCancel}
          primaryLabel={t('common.continue', 'Weiter')}
          onPrimary={startSetup}
          primaryDisabled={busy}
        />
      </div>
    )
  }

  if (stage === 'scan' && setup) {
    return (
      <div style={{ padding: 24, maxWidth: 480 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600 }}>
          {t('auth.setup.scanQR', 'QR-Code scannen')}
        </h2>
        <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
          <img
            src={setup.qr_code_base64}
            alt={t('auth.setup.scanQR', 'QR-Code scannen')}
            style={{ width: 200, height: 200, border: '1px solid #e4e4e7', borderRadius: 8 }}
            data-testid="setup-qr-image"
          />
        </div>
        <div style={{ margin: '8px 0', fontSize: 12, color: '#71717a', textAlign: 'center' }}>
          {t('auth.setup.orEnterCode', 'Oder Code manuell eingeben')}
          <code style={{
            display: 'block', margin: '6px auto 0', padding: '6px 10px',
            background: '#f4f4f5', borderRadius: 6, fontSize: 13, color: '#18181b',
            wordBreak: 'break-all',
          }} data-testid="setup-secret">{setup.secret}</code>
        </div>
        <p style={{ margin: '20px 0 8px', fontSize: 14, fontWeight: 500 }}>
          {t('auth.setup.verifyCode', '6-stelligen Code aus der App eingeben')}
        </p>
        <OtpInput value={otp} onChange={setOtp} onComplete={submitOtp} disabled={busy} />
        {err && <ErrorBanner msg={err} />}
        <FooterButtons
          onCancel={onCancel}
          primaryLabel={t('auth.setup.verify', 'Bestätigen')}
          onPrimary={() => submitOtp(otp)}
          primaryDisabled={busy || otp.length !== 6}
        />
      </div>
    )
  }

  // stage === 'codes'
  if (!codes) return null
  return (
    <div style={{ padding: 24, maxWidth: 480 }}>
      <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#b08e2d' }}>
        {t('auth.recoveryCodes.title', 'Recovery-Codes speichern')}
      </h2>
      <div style={{
        margin: '0 0 16px', padding: 12, border: '1px solid #f5d76a',
        background: '#fef9c3', borderRadius: 6, fontSize: 13, color: '#854d0e',
      }}>
        {t('auth.recoveryCodes.warning',
           'Diese Codes werden nur EINMAL angezeigt. Sichere sie jetzt — jeder Code funktioniert genau einmal.')}
      </div>
      <ul data-testid="recovery-codes-list" style={{
        listStyle: 'none', padding: 12, margin: '0 0 16px',
        background: '#fafafa', borderRadius: 8, fontFamily: 'monospace', fontSize: 14,
      }}>
        {codes.recovery_codes.map((c) => (
          <li key={c} style={{ padding: '2px 0' }}>{c}</li>
        ))}
      </ul>
      <div style={{ display: 'flex', gap: 8, margin: '0 0 16px' }}>
        <button type="button" onClick={downloadCodes} style={btnSecondary}
          data-testid="recovery-codes-download">
          {t('auth.recoveryCodes.download', 'Download')}
        </button>
        <button type="button" onClick={copyCodes} style={btnSecondary}
          data-testid="recovery-codes-copy">
          {t('auth.recoveryCodes.copy', 'Kopieren')}
        </button>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '0 0 16px' }}>
        <input type="checkbox" checked={savedAck} onChange={(e) => setSavedAck(e.target.checked)}
          data-testid="recovery-codes-ack" />
        <span>{t('auth.recoveryCodes.confirmSaved', 'Ich habe die Codes sicher gespeichert')}</span>
      </label>
      <button type="button"
        onClick={onDone}
        disabled={!savedAck}
        data-testid="recovery-codes-continue"
        style={{
          width: '100%', padding: '10px 16px', borderRadius: 8, border: 'none',
          background: savedAck ? 'var(--brand-primary)' : '#a1a1aa', color: 'var(--brand-on-primary)',
          fontSize: 14, fontWeight: 600, cursor: savedAck ? 'pointer' : 'not-allowed',
        }}>
        {t('auth.recoveryCodes.continue', 'Weiter')}
      </button>
    </div>
  )
}

function ErrorBanner({ msg }: Readonly<{ msg: string }>) {
  return (
    <div role="alert" style={{
      margin: '12px 0', padding: '8px 12px', borderRadius: 6,
      background: '#fef2f2', color: '#991b1b', fontSize: 13,
    }}>{msg}</div>
  )
}

function FooterButtons({
  onCancel, primaryLabel, onPrimary, primaryDisabled,
}: Readonly<{
  onCancel?: () => void; primaryLabel: string;
  onPrimary: () => void; primaryDisabled?: boolean;
}>) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
      {onCancel && (
        <button type="button" onClick={onCancel} style={btnSecondary}>
          {t('common.cancel', 'Abbrechen')}
        </button>
      )}
      <button type="button" onClick={onPrimary} disabled={primaryDisabled}
        style={{
          flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none',
          background: primaryDisabled ? '#a1a1aa' : 'var(--brand-primary)',
          color: 'var(--brand-on-primary)', fontSize: 14, fontWeight: 600,
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
        }}>{primaryLabel}</button>
    </div>
  )
}

const btnSecondary: React.CSSProperties = {
  padding: '8px 14px', borderRadius: 8, border: '1px solid #d4d4d8',
  background: '#fff', color: '#18181b', fontSize: 14, cursor: 'pointer',
}
